import { fn, INamed } from '../utils';
import FindFirst = fn.FindFirst;
import ToDictionary = fn.ToDictionary;
import ToDictionaryEx = fn.ToDictionaryEx;
import { IPageObj, TDefaultPageObj, IPageProperty, NavSyncMode, IPageMetaData, NavDtFormat, NumericType } from '../nav/navtypes';
import { getClientKeyFunc, Model, setModel, valHasChanged, __ValidateProps, enforceTyped } from './navmodel';
import { NavPageItems } from './navpageitems';
import { NavPropertyItem } from './navpropertyitem';
import { NavItemAnchor } from './navitemanchor';
import { NavClientService } from './navclientservice';
import { NavLineData } from './navlinedata';
import { INavPageClientInfo } from './navmodel';
import { NavData, IsNavPageItem, RegisterIndirection } from './indirection';
import { INavKey, IPagePropertyDependency, INavMethodConfig, IPagePropertyDependencyItem } from './navdata';
import { NavPropertyContainer } from '@rwfangularjs/navdata';

/** A Root Item for a Nav "Page" Data Item */
export class NavPageItem<TPage extends IPageObj = TDefaultPageObj, TPageName extends string = string> extends NavPropertyItem<TPage> {
    /** Optional Referennce to Container of items if created via ReadMultiple */
    public Container?: NavPageItems<TPage>;
    /** Reference to Items Anchor (owned or via Opt. NavPageItems Parent Instance) */
    public Anchor: NavItemAnchor<TPage>;
    constructor(public ClientInfo: INavPageClientInfo<TPage, TPageName>, public Service: NavClientService, /** Reference for 'ContextBound' DMS UI Line Handling! */
        Model: Model & Readonly<TPage>, private serverData: Object, Anchor?: NavItemAnchor<any>) {
        super(null, ClientInfo.MetaData, Model);
        // Anchor is External for Container Objects (ReadMultiple), Owned for Standalone (Read)
        this.Anchor = Anchor || new NavData.NavItemAnchor(Service, this);
        this.IsInSync = true;
        this.PageName = ClientInfo.MetaData.PageName;
        // Initiailize Line Element Access
        this.MetaData.Properties.filter(item => !!item.ArrayProperties).forEach(arrProp => {
            var values = <any[]>this.Model[arrProp.Name];
            var lineData = this.GetLineData(arrProp);
            lineData.NotifyAdded(values.map(value => new NavPropertyItem<IPageObj>(this, arrProp.ArrayProperties, value)));
        }, this);
        ClientInfo.MetaData.Instantiate(this);
    }

    private GetIsAutoUpdate(prop: IPageProperty, explicit: boolean) {
        if (typeof explicit == "boolean")
            return !!explicit;

        var propValue = prop.AutoUpdate;

        if (propValue === null || propValue == undefined) {
            var dValue = this.MetaData.DefaultAutoUpdate;
            if (dValue == null || dValue == undefined)
                return false; // Global Default: no AutoUpdate
            return !!dValue;
        }

        return !!propValue;
    }

    /** Register Dependcies */
    public RegisterDependency<TSource = any>(dependencies: IPagePropertyDependencyItem<any, any>[], ctx: TSource) {
        var normalized = dependencies.map(dep => {
            if (fn.isFunction(dep.DestProperties))
                var destProperties = <string[]>(<Function><any>dep.DestProperties)(this);
            else
                var destProperties = <string[]>dep.DestProperties;
            var sourceProp = <IPageProperty>undefined,
                metaData = this.MetaData;
            if (dep.SourcePath) {
                sourceProp = metaData.ResolveProperty(<any>dep.SourcePath, __ValidateProps);
                if (!sourceProp) {
                    return; // Property Defined, but not found
                }
            }
            var resolvedDestProperties = (destProperties || []).map(item => metaData.ResolveProperty(<any>item, __ValidateProps), this).filter(fn.isSet);
            return <IPagePropertyDependency<any, any, TSource>>{
                Context: dep.InitContext && dep.InitContext(ctx),
                Enabled: dep.Enabled,
                Mandatory: dep.Mandatory,
                Visible: dep.Visible,
                SourceProperty: sourceProp,
                DestPropertyItems: resolvedDestProperties
            };
        }, this).filter(fn.isSet);

        normalized.forEach(this.refreshDependency, this);
        this._dependencies.push(...normalized);
    }
    private _dependencies: IPagePropertyDependency[] = [];

    private refreshDependency(item: IPagePropertyDependency) {
        var value = undefined;
        if (!item.SourceProperty) {
            // No source Dep

        } else if (item.SourceProperty.ParentProp) {
            // Child Prop
            // TODO : Iteralte all Lines
        } else {
            // A Root Prop
            value = this.GetValueEx(item.SourceProperty);
        }

        var id = "_dependencies" + (item["_Id"] || (item["_Id"] = Math.random().toString()));

        var hidden = item.Visible ? !item.Visible(this, value, item.Context) : null;
        var mandatory = item.Mandatory ? !!item.Mandatory(this, value, item.Context) : null;

        var calcEnabledValue = item.Enabled ? item.Enabled(this, value, item.Context) : null;
        var readonly = calcEnabledValue !== null ? !calcEnabledValue : null;
        let operations = this.Root.Anchor;
        if (hidden !== null) {
            operations.InVisible.ReplaceValues(id, hidden ? item.DestPropertyItems : []);
        }
        if (mandatory != null) {
            operations.Mandatory.ReplaceValues(id, mandatory ? item.DestPropertyItems : []);
        }

        if (readonly != null) {
            operations.Readonly.ReplaceValues(id, readonly ? item.DestPropertyItems : []);
        }
    }

    /** Marker : Is In Sync with Server */
    public IsInSync: boolean;
    /** Marker : IsDeletedInNav */
    public IsDeletedInNav: boolean;
    /** Marker : Element is New On Client, not yet persisted to NAV */
    public IsNew: boolean;
    /** PageName this Item was Loaded From */
    public readonly PageName: TPageName;
    // Internal : starting point for user based Value Change Notification
    NotifyValueChanged(item: NavPropertyItem, prop: IPageProperty) {
        this.IsInSync = false;
        item.Root.Anchor.FireValueChanged(item, prop);

        this.RefreshDependencies();
    }

    RefreshDependencies() {
        // TODO : Optimize Dependency Calculation
        this._dependencies.forEach(d => this.refreshDependency(d));
    }

    public HasLineData(prop: keyof TPage | IPageProperty) {
        prop = this.ResolveProperty(prop, false);
        if (prop && prop.ArrayProperties)
            return this.lineData[prop.Name];
    }

    /** Get Line Data Wrapper for an Array Typed Property (e.g. PurchLines, SalesLines) */
    public GetLineData(prop: keyof TPage | IPageProperty) {
        prop = this.ResolveProperty(prop, false);
        if (prop && prop.ArrayProperties)
            return this.lineData[prop.Name] || (this.lineData[prop.Name] = new NavLineData(this, prop));
    }
    private lineData: INamed<NavLineData> = <any>{};

    /** INTERNAL : set Value on an Item Bound to this root Item (Item itself or a Line Item)
      - if other SetOperation(s)  still pending, it will be deferred after that!
      - Handles AutoUpdate!
      */
    public SetBoundValue(onItem: NavPropertyItem, propOrPropName: IPageProperty | string, uncheckedValue: any, explicitAutoUpdate?: boolean) {
        let Service = this.Service,
            $c = Service.$c;

        try {
            let prop = onItem.ResolveProperty(propOrPropName, true),
                oldValue = onItem.GetValue(prop.Name),
                checkedValue = Service.PrepareServerValue(onItem, prop, enforceTyped(prop, uncheckedValue));

            let hasChanged = valHasChanged(prop, oldValue, checkedValue);

            if (!hasChanged)
                return $c.when();

            let result = $c.when();

            // Decimal Values: max precision = 2
            if ((prop.DecimalNumericType == NumericType.Currency) && (typeof checkedValue === "number")) {
                if (parseFloat(checkedValue.toFixed(2)) !== checkedValue) {
                    const e = { Item: onItem, Prop: prop, Value: checkedValue, Answer: <boolean | Promise<boolean>>false };
                    onItem.Root.Anchor.trigger("OnExceedCurrencyValuePrecision", e);
                    result = result.then(() => e.Answer).then((answer: boolean) => {
                        if (!answer)
                            return $c.reject(`Betrag für '${prop.Title}' kann nicht mehr als 2 Nachkommastellen beinhalten, '${uncheckedValue}' ist ein ungültiger Wert!`);
                    });
                }
            }

            return result.then(() => {
                // Acutally Apply the Value to Model. opt. Sync if configured
                let applyValue = () => {
                    setModel(onItem.Model, prop.Name, checkedValue);
                    if (prop.SyncMode == NavSyncMode.Sync && IsNavPageItem(onItem) && onItem.Container) {
                        onItem.Container.Items.forEach(item => setModel(item.Model, prop.Name, checkedValue));
                        fireValueChangedItems = onItem.Container.Items.map(fn.identity);
                    }
                },
                    fireValueChangedItems = [onItem];

                if (prop.ReadOnly || this.Root.Anchor.Readonly.GetValue(prop, onItem))
                    throw Error("Eigenschaft '" + prop.Name + "[" + prop.Title + "]' ist schreibgeschützt und kann nicht geändert werden");
                if (this.GetIsAutoUpdate(prop, explicitAutoUpdate)) {
                    // AutoUpdate Necessary
                    return this.Update(applyValue).then(newItem => {
                        var loadValue: NavPropertyItem = newItem;
                        if (onItem.MetaData.PropertyName) {
                            loadValue = onItem.Root.GetLineData(onItem.MetaData.PropertyName).GetItems(onItem.ClientKey)[0];
                        }
                        if (loadValue && !valHasChanged(prop, checkedValue, loadValue.Model[prop.Name])) {
                            fireValueChangedItems.forEach(cItem => {
                                 onItem.Root.Anchor.FireValueChanged(cItem, prop);
                                 onItem.Root.RefreshDependencies();
                                });
                        }
                    });
                } else {
                    // Set Value on Client
                    applyValue();
                    fireValueChangedItems.forEach(cItem => this.NotifyValueChanged(cItem, prop));
                    return $c.when();
                }
            });
        } catch (err) {
            return $c.reject(err);
        }
    }

    /** Start Update after Currently Running updates finshed, onUpdate Action will be called right before this Update Operation gets started */
    public Update(OnUpdate?: () => void, noSync?: boolean) {
        return this.Anchor.Update(OnUpdate, noSync).then(item => this);
    }

    /** Start Method Execution after Currently Running updates finshed, onUpdate Action will be called right before this Update Operation gets started
 * Return value : "Result" : Result of Method
 * TODO :
 * - Include Out Parameters of Method on Server Side)
 * - Do Not ReRead Item on Server Side if not configured (TODO : extend configuration)
  */
    public ExecuteMethod(method: INavMethodConfig, OnUpdate?: () => void, noEnqueue?: boolean): PromiseLike<{ [name: string]: any }> {
        return this.Anchor.ExecuteMethod(method, OnUpdate, noEnqueue);
    }

    /** Reload State from Server Cancelling current outstanding Values*/
    public RevertItem(force?: boolean, ignoreIfDeleted?: boolean) : PromiseLike<this> {
        // Dispatch to Container if applicaple
        var source = this.Anchor.Source;
        if (source instanceof NavPageItems)
            return source.RevertItem(force, ignoreIfDeleted).then(fn.ToFunc(this));

        if (force || this.GetCurrentServerChanges(true))
            return this.Service.LoadItem<TPage, TPageName>(this.CreateKey(), this.ClientInfo.FromStaticDescription).then(item => {
                if ((!item) && (ignoreIfDeleted != true)) {
                    throw new Error("Das Element ist nicht mehr vorhanden!");
                }
                if (item)
                    this.Sync(item);
                return this;
            });
        else
            return this.Service.$c.when(this);
    }

    /*** Create Key Information for this Item (as the Nav "Key" might change */
    public CreateKey() {
        var clientInfo = this.ClientInfo,
            instanceId = this.Anchor && this.Anchor.InstanceID,
            keys = fn.ToDictionaryEx(clientInfo.MetaData.KeyFields, fn.identity, k => this.GetValue(<any>k));
        var result = <INavKey<TPage, TPageName>>{
            Keys: keys,
            PageName: clientInfo.MetaData.PageName,
            NavUrl: clientInfo.Url
        };

        if (instanceId) {
            result.InstanceID = instanceId;
        }
        return result;
    }

    /** Insert New Row Item after Pending Operations finished (wOut Prop : new "Main Line Prop") */
    public InsertNewRowItem(prop?: any) {
        if (!prop)
            prop = this.ClientInfo.MasterLineProperty;

        var p = this.ResolveProperty(prop, false);
        if (!p || !p.ArrayProperties)
            throw new Error("InsertNewRowItem : Invalid Property or Property not found!");

        var pGetKey = getClientKeyFunc(p.ArrayProperties);
        var current = ToDictionary(this.Model[p.Name], item => pGetKey(item));
        return this.Anchor.EnqueueOperation(() => this.Service.InsertNewRowItem(this, p.Name).then(reloaded => {
          var result = this.Model[p.Name].find((item: Model) => !current[pGetKey(item)]);
            if (!result)
                throw new Error("InsertNewRowItem: Keine neue Zeile erhalten!");
            return this.GetLineData(p).GetItems(result)[0];
        }));
    }

    /** Delete a Row Item (NOT Enqueued) */
    public DeleteRowItem(outerRowItem: NavPropertyItem, lineData: NavLineData): PromiseLike<NavPageItem> {
        var root = outerRowItem.Root;
        if (root !== this)
            return root.DeleteRowItem(outerRowItem, root.GetLineData(lineData.Property));
        var key = outerRowItem.ClientKey,
            Service = this.Service;
        var validatedRowItem = lineData.GetItems(key)[0];
        if (!validatedRowItem)
            return Service.$c.reject("DeleteRowItem: Not found [" + key + "]");
        return Service.DeleteRowItem(validatedRowItem).then(reloaded => this);
    }

    /** Duplicate a Row Item (NOT Enqueued)*/
    public DuplicateRowItem(outerRowItem: NavPropertyItem, lineData: NavLineData): PromiseLike<NavPageItem> {
        if (outerRowItem.Root !== this)
            return outerRowItem.Root.DuplicateRowItem(outerRowItem, outerRowItem.Root.GetLineData(lineData.Property));
        var key = outerRowItem.ClientKey,
            Service = this.Service;
        var validatedRowItem = lineData.GetItems(key)[0];
        if (!validatedRowItem)
            return Service.$c.reject("DuplicateRowItem: Not found [" + key + "]");
        return Service.DuplicateRowItem(validatedRowItem).then(reloaded => this);
    }

    /** Sync Key Values only from Reloaded Object from Server */
    SyncKeys(serverItem: IPageObj | IPageObj[]) {
        var singleItem: IPageObj;
        if (Array.isArray(serverItem)) {
            if (serverItem.length != 1)
                throw new Error("Expected Single ServerItem");
            singleItem = serverItem[0];
        } else {
            singleItem = serverItem;
        }
        var newKey = singleItem.Key;
        if (this.Model.Key !== newKey || this.Key !== newKey) {
            setModel(this.Model, "Key", newKey);
            this.Root.Anchor.FireValueChanged(this, this.MetaData.PropsByName["Key"]);
        }
    }

    /** Apply State from Reloaded Object from Server */
    Sync(serverItem: NavPageItem | NavPageItem[]) {
        if (Array.isArray(serverItem)) {
            if (serverItem.length != 1)
                throw new Error("Expected Single ServerItem");
            serverItem = serverItem[0];
            if (!serverItem) {
                // Item Got Deleted
                this.IsDeletedInNav = true;
                return;

            }
        }
        this.assign(serverItem);
        this.serverData = serverItem.serverData;
        this.IsInSync = true;
    }

    /** Get a Serializable Object to Send to Server inlcuding Key Representation (technical + Configured) and currently 'Open' Changes (<>) */
    public GetCurrentServerChanges(ignoreIfNoChanges?: boolean) {
        var changeConfig = { withKeys: true, hasChanges: false };
        var result = NavPropertyItem.GetChanges(this, this.serverData, this.MetaData, changeConfig);
        return ignoreIfNoChanges && !changeConfig.hasChanges ? undefined : result;
    }

    /** Retrieve Data to be sent to Server for update */
    static getServerData(pageMetaData: IPageMetaData, value: Model) {
        var result = {};
        var definedNames = ToDictionaryEx(Object.getOwnPropertyNames(value), item => item, item => true);
        pageMetaData.Properties.filter(item => definedNames[item.Name]).forEach(item => {
            var setValue = value[item.Name];
            switch (item.Type) {
                case "DateTime":
                    var dtValue = <Date>setValue;
                    if (dtValue == undefined || dtValue == null) {
                        // OK
                    }
                    else if (dtValue && dtValue.toISOString) {
                        if (isNaN(dtValue.valueOf()))
                            setValue = null;
                        else {
                            switch (item.DateFormat) {
                                case NavDtFormat.DateAndTime:
                                    setValue = dtValue.toISOString();
                                    break;
                                case NavDtFormat.Time:
                                    setValue = dtValue.toISOString();
                                    break;
                                default:
                                    setValue = fn.ToDateString(dtValue);
                            }
                        }
                    }
                    else
                        throw "invalid Date value : " + setValue;
                    break;

                case "Int32":
                    if (setValue == undefined || setValue == null)
                        setValue = 0;
                    break;
                default:
                    if (item.ArrayProperties) {
                        if (setValue) {
                            setValue = setValue.map(d => NavPageItem.getServerData(item.ArrayProperties, d));
                        }
                    }
            }

            if (setValue !== undefined)
                result[item.Name] = setValue;
        });

        return result;
    }
}
RegisterIndirection("NavPageItem", NavPageItem);
